import { TaskVideoPreview } from "./task-video-preview";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ProForm,
  ProFormDigit,
  ProFormRadio,
  ProFormSelect,
  ProFormText,
  ProFormTextArea,
} from "@ant-design/pro-components";
import {
  App,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Steps,
  Skeleton,
  theme,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  MessageOutlined,
  AppstoreOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { post, put, query } from "./api";
import { businessTaskTypes, taskBasePath, taskPath, taskBusiness, type TaskBusiness } from "./business";
import {
  money,
  isTaskClosed,
  typeLabels,
  type Agent,
  type Task,
} from "./domain";
import { useResource } from "./shared";
import { fromTask, payloadFromValues, distributeQuotas, defaultTaskType, type Values } from "./task-form";
import { MaterialPicker, MaterialImage } from "./materials";
import { TaskPreview } from "./task-preview";
import { TaskPhonePreview } from "./task-phone-preview";
import { TaskWritingGuide } from "./task-writing-guide";
import { TaskTextEditor, TaskFormattedText } from "./task-text-editor";
import { TaskStepsEditor } from "./task-steps-editor";
import { previewWithDefaultSteps, usesDefaultTaskSteps, type TaskStepPolicy } from "./task-step-policy";
import { MessageImport } from "./message-import";
import "./task-composer.css";
import { amountToMinor, taskBudgetMinor } from "./finance-domain";
import { checkTaskFunding, TaskFundingCheck } from "./finance-funding";
export { payloadFromValues } from "./task-form";
dayjs.extend(utc);
dayjs.extend(timezone);
const stepNames = [
  ["title", "task_type", "description", "category", "platform", "message_import"],
  ["steps"],
  [
    "price",
    "quantity",
    "dates",
    "app_id",
    "bundle_id",
    "app_name",
    "app_version",
    "app_price",
    "app_artwork_url",
    "app_developer_name",
    "keyword_quotas",
  ],
  ["change_note"],
];
export function TaskEditor({ agent, business }: { agent: Agent; business: TaskBusiness }) {
  const { id } = useParams();
  // Each route owns its form, wizard and request state; stale task responses
  // cannot populate a different task or a newly created draft.
  return <TaskEditorForm key={`${business}:${id || "new"}`} id={id} agent={agent} business={business} />;
}
function TaskEditorForm({ agent, id, business }: { agent: Agent; id?: string; business: TaskBusiness }) {
  const { token } = theme.useToken();
  const label = business === "sms" ? "iMessage 任务" : "App 推广任务";
  const allowed = businessTaskTypes(agent, business);
  const initialTaskType = defaultTaskType(agent, business);
  const editing = !!id;
  const resource = useResource<Task>(editing ? `/tasks/${id}${query({ business })}` : null);
  const policy = useResource<TaskStepPolicy>(`/task-policy${query({ business })}`);
  const [form] = Form.useForm<Values>();
  const [step, setStep] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (previousStep.current !== step)
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    previousStep.current = step;
  }, [step, business]);
  const [busy, setBusy] = useState(false);
  const savingRef = useRef(false);
  const published = !!resource.data?.formal_task_id;
  const [importBusy, setImportBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const { message } = App.useApp();
  const navigate = useNavigate();
  const type = Form.useWatch("task_type", form) || initialTaskType;
  useEffect(() => {
    if (type === "download" || type === "message_send")
      form.setFieldValue("platform", "ios");
  }, [type, form]);
  const allValues = Form.useWatch([], { form, preserve: true }) || {};
  const requireReview = business === "sms" || (agent.promotion_require_review ?? true);
  const stepMode = Form.useWatch("steps_mode", { form, preserve: true }) || "default";
  const inputPayload = { ...payloadFromValues({ ...form.getFieldsValue(true), ...allValues, task_type: type || (business === "sms" ? "message_send" : "other_normal") } as Values, editing ? resource.data?.payload : undefined, false), require_review: requireReview };
  const useDefaults = usesDefaultTaskSteps(inputPayload);
  const previewPayload = previewWithDefaultSteps(inputPayload, policy.error ? undefined : policy.data);
  const previewAssets = { ...resource.data?.asset_urls, ...policy.data?.asset_urls };
  const quotas = Form.useWatch("keyword_quotas", form);
  const price = Form.useWatch("price", form) || 0;
  const messageImport = Form.useWatch("message_import", { form, preserve: true });
  const enteredQuantity = Form.useWatch("quantity", form) || 0;
  const quantity = type === "download" ? (quotas || []).reduce((sum: number, q: { target_count?: number }) => sum + (q.target_count || 0), 0) : type === "message_send" ? (messageImport?.id ? messageImport.count : resource.data?.payload.max_assignments || 0) : enteredQuantity;
  const budgetMinor = taskBudgetMinor(amountToMinor(price) ?? 0, quantity);
  useEffect(() => {
    if (editing && resource.data && taskBusiness(resource.data.payload.task_type) === business)
      form.setFieldsValue(fromTask(resource.data.payload));
  }, [resource.data, editing, form, business]);
  const [videoUploads, setVideoUploads] = useState<Record<number, boolean>>({});
 const save = async (submit: boolean) => {
 if (Object.values(videoUploads).some(Boolean)) {message.warning("请等待视频上传和处理完成后再保存");return;}
    if (savingRef.current || busy || importBusy) return;
    savingRef.current = true;
    setSaveError("");
    try {
      if (!policy.data?.price_range || policy.loading || policy.error) { message.warning("请先加载平台价格区间后再保存"); return; }
      const amount = amountToMinor(form.getFieldValue("price")) ?? 0;
      if (!published && (amount < policy.data.price_range.min_amount_minor || amount > policy.data.price_range.max_amount_minor)) { message.error("金额超出平台价格区间，请修改报价"); setStep(2); return; }
      if (submit && useDefaults && (policy.loading || policy.error || !policy.data)) { message.warning("请先加载平台默认步骤，再核对并提交审核"); setStep(1); return; }
      await form.validateFields(submit ? undefined : ["title", "task_type"]);
      const values = form.getFieldsValue(true);
      const payload = payloadFromValues(
        values,
        editing ? resource.data?.payload : undefined,
        submit,
        { business, allowed, requireReview },
      );
      setBusy(true);
      if (submit) await checkTaskFunding(taskBudgetMinor(payload.reward_amount_minor, payload.max_assignments), published);
      let task: Task;
      if (editing)
        task = await put<Task>(`/tasks/${id}${query({ business })}`, {
          payload,
          incomplete: !submit,
          change_note: values.change_note || "修改任务内容",
          expected_version: resource.data?.version,
        });
      else
        task = await post<Task>(`/tasks${query({ business })}`, {
          payload,
          incomplete: !submit,
          change_note: values.change_note || "创建任务",
        });
      if (submit) {
        try {
          task = await post<Task>(`/tasks/${task.id}/submit${query({ business })}`, {
            expected_version: task.version,
          });
        } catch (e) {
          message.warning(`草稿已保存，提交审核未成功：${(e as Error).message}`);
          navigate(taskPath(business, task.id));
          throw e;
        }
      }
      message.success(submit ? published ? "任务已提交审核，沿用已冻结预算" : "任务已提交审核，所需预算已冻结" : "草稿已保存");
      navigate(taskPath(business, task.id));
    } catch (e) {
      if (e instanceof Error) setSaveError(e.message);
      else {
        const firstField = (
          e as { errorFields?: { name: (string | number)[] }[] }
        ).errorFields?.[0]?.name[0];
        const targetStep = stepNames.findIndex((names) =>
          names.includes(String(firstField)),
        );
        if (targetStep >= 0) setStep(targetStep);
        const fieldName = (e as { errorFields?: { name: (string | number)[] }[] }).errorFields?.[0]?.name;
        if (fieldName) requestAnimationFrame(() => form.scrollToField(fieldName, { block: "center", behavior: "smooth", focus: true }));
        message.warning("请检查并补全所有必填字段");
      }
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  };
  const next = async () => {
    if (importBusy) return;
    try {
      if (step === 1 && useDefaults && (policy.loading || policy.error || !policy.data)) { message.warning("请先加载平台默认步骤，或选择自定义步骤"); return; }
      const names = stepNames[step] || [];
      const filtered = names
        .filter(
          (name) =>
            type === "download" ||
            (!name.startsWith("app_") &&
              name !== "bundle_id" &&
              name !== "keyword_quotas"),
        )
        .filter((name) => ["normal", "other_normal"].includes(type || "") || !["completion_screenshot_count", "required_screenshots"].includes(name));
      await form.validateFields(filtered, { recursive: true });
      setStep(step + 1);
    } catch (error) {
      message.warning("请先补全当前步骤的必填字段");
      const first = (error as { errorFields?: { name: (string | number)[] }[] }).errorFields?.[0]?.name;
      if (first) form.scrollToField(first, { block: "center", behavior: "smooth", focus: true });
    }
  };
  if (editing && resource.loading && !resource.data) return <Card loading />;
  if (editing && resource.error)
    return (
      <Alert
        showIcon
        type="error"
        message={resource.error}
        action={<Space><Link to={taskBasePath(business)}>返回{label}列表</Link><Button onClick={() => void resource.reload()}>重试</Button></Space>}
      />
    );
  if (!allowed.length || (editing && resource.data && (taskBusiness(resource.data.payload.task_type) !== business || !allowed.includes(resource.data.payload.task_type))))
    return <Alert showIcon type="warning" message="此任务不在当前业务的可编辑范围内" action={<Link to={taskBasePath(business)}>返回{label}列表</Link>} />;
  if (editing && resource.data && isTaskClosed(resource.data)) return <Alert showIcon type="info" message="任务已结束，不能再修改或重新提交" description="请新建任务继续投放；本任务可查看资金与结算记录。" action={<Link to={taskPath(business, id)}>返回任务详情</Link>} />;
  if (editing && resource.data?.status === "pending_review")
    return (
      <Alert
        showIcon
        type="warning"
        message="审核期间暂不可编辑"
        action={<Link to={taskPath(business, id)}>返回任务详情</Link>}
      />
    );
  const appearanceFields = <>
    {business === "sms" && <ProFormText name="description_title" label="任务说明标题" placeholder="任务说明" fieldProps={{ maxLength: 60 }} />}
    <Form.Item name="icon_url" label="任务图标（选填）"><MaterialPicker module="task_icon" assetURLs={resource.data?.asset_urls} label="选择或上传任务图标" /></Form.Item>
    <Form.Item noStyle shouldUpdate>{() => <Input aria-label="任务图标 HTTPS 辅助链接" placeholder="或粘贴 HTTPS 图片链接后按回车" onPressEnter={event => { event.preventDefault(); const input = event.currentTarget; if (/^https:\/\//i.test(input.value.trim())) { form.setFieldValue("icon_url", input.value.trim()); input.value = ""; } else message.warning("请输入 HTTPS 图片链接"); }} style={{ marginBottom: 24 }} />}</Form.Item>
    <ProFormSelect name="tags" label="任务标签" fieldProps={{ mode: "tags", maxTagCount: 6 }} placeholder="输入标签后按回车，选填" />
  </>;
  const importedCount = allValues.message_import?.count || 0;
  return (
    <div className={`message-task-editor ${business === "promotion" ? "promotion-task-editor" : ""}`} style={{
      "--composer-accent": token.colorPrimary, "--composer-tint": token.colorPrimaryBg,
      "--composer-text": token.colorText, "--composer-muted": token.colorTextSecondary,
      "--composer-line": token.colorBorderSecondary, "--composer-bg": token.colorBgContainer,
      "--composer-fill": token.colorFillQuaternary,
    } as CSSProperties}>
      {editing && <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, marginBottom: 12 }}
        onClick={() => navigate(taskPath(business, id))}
      >
        返回{label}详情
      </Button>}
      <header className="message-task-heading">
        <div className="message-task-heading-main"><span className="message-task-mark">{business === "sms" ? <MessageOutlined /> : <AppstoreOutlined />}</span><div><h1>{editing ? "编辑" : "新建"} {label}</h1><p>{business === "sms" ? "导入收件人与内容，完成设置后提交审核。" : "按步骤填写推广内容与投放设置，核对后提交审核。"}</p></div></div>
        <Button icon={<SaveOutlined />} loading={busy} disabled={importBusy || agent.business_frozen} onClick={() => void save(false)}>保存草稿</Button>
      </header>
      {agent.business_frozen && (
        <Alert
          type="warning"
          showIcon
          message="业务已冻结，暂不可创建或修改任务"
          style={{ marginBottom: 20 }}
        />
      )}
      {editing && resource.data?.formal_task_id && (
        <Alert
          type="info"
          showIcon
          message="任务修改后需重新审核"
          description="单次奖励、任务名额和任务类型已锁定。修改任务内容需重新审核，沿用原冻结预算；已有领取按原任务内容继续。"
          style={{ marginBottom: 20 }}
        />
      )}
      <Modal style={{ top: 24 }} className="task-phone-modal" title="手机实时预览" width={440} open={previewOpen} onCancel={() => setPreviewOpen(false)} footer={<Button onClick={() => setPreviewOpen(false)}>返回编辑</Button>}><TaskPhonePreview assetURLs={previewAssets} payload={previewPayload} /><p className="task-preview-note">内容实时更新。设备字体、领取状态和验证流程以正式 App 为准。</p></Modal>
      <Modal title="任务填写图文指南" width={1060} open={guideOpen} onCancel={() => setGuideOpen(false)} footer={<Button type="primary" onClick={() => setGuideOpen(false)}>开始填写我的任务</Button>}><TaskWritingGuide /></Modal>
      {business === "promotion" && <div className="task-composer-intro"><div><strong>任务内容与展示预览</strong><p>填写任务信息、操作步骤及报价设置，可通过手机预览查看会员端展示效果。</p></div><Space wrap><Button onClick={() => setGuideOpen(true)}>图文填写示例</Button><Button className="task-preview-mobile" onClick={() => setPreviewOpen(true)}>手机实时预览</Button></Space></div>}
      <nav className="message-task-progress" aria-label="任务创建步骤"><Steps className="sms-editor-steps" responsive={false} size="small"
          current={step}
          items={[
            { title: "任务内容", description: business === "sms" ? "名称与收件人" : "名称、说明与图标" },
            { title: "执行步骤", description: "平台默认或自定义" },
            { title: "投放配置", description: "数量、奖励与时间" },
            { title: "确认提交", description: "核对后交由平台审核" },
          ]}
        /></nav>
      {useDefaults && step !== 1 && policy.error && <Alert style={{ marginBottom: 16 }} type="error" showIcon message="默认步骤加载失败，请重试后核对预览" action={<Button onClick={() => void policy.reload()}>重试</Button>} />}
      <div ref={editorRef} className={business === "promotion" ? "task-composer-grid message-composer-grid" : "message-composer-grid"}><Card className="editor-card">
        <ProForm<Values>
          form={form}
          submitter={false}
          layout="vertical"
          initialValues={{
            task_type: initialTaskType,
            platform: "ios",
            category: "experience",
            price: 1,
            quantity: 100,
            completion_screenshot_count: 0,
            steps_mode: "default",
            steps: [],
            search_keywords: [],
            keyword_quotas: [],
          }}
        >
          <div
            className="editor-section"
            style={{ display: step === 0 ? "block" : "none" }}
          >
            <h3>{business === "sms" ? "任务与收件人" : "任务基本信息"}</h3>
            <p className="muted">
              {business === "sms" ? "先给任务命名，再导入需要发送的信息。" : "名称和描述会直接影响会员对任务的理解，请准确填写。"}
            </p>
            <ProFormText
              name="title"
              label="任务名称"
              placeholder={business === "sms" ? "例如：十月客户预约确认" : "给任务起一个清晰易懂的名称"}
              fieldProps={{ maxLength: 120, showCount: true }}
              rules={[
                { required: true, whitespace: true, message: "请输入任务名称" },
                { max: 120, message: "最多 120 字" },
              ]}
            />
            {business === "sms" && <Form.Item name="message_import" label="收件人与发送内容" required={!resource.data?.formal_task_id} rules={[{ validator: (_, value) => resource.data?.formal_task_id || value?.id && value?.count > 0 ? Promise.resolve() : Promise.reject(new Error("请先导入收件人（手机号码或邮箱）和 iMessage 内容")) }]}>
              <MessageImport published={!!resource.data?.formal_task_id} onBusyChange={setImportBusy} />
            </Form.Item>}
            {business === "sms" ? <>
              <Form.Item name="task_type" hidden><Input /></Form.Item>
              <div className="message-editor-subheading"><h3>任务展示信息</h3><p>向执行任务的会员说明要做什么，以及需要注意的事项。</p></div>
            </> : <ProFormRadio.Group
              name="task_type"
              disabled={published}
              label="推广任务类型"
              options={allowed.map(value => ({ value, label: typeLabels[value] }))}
              rules={[{ required: true, message: "请选择推广任务类型" }]}
            />}
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <ProFormSelect
                  name="platform"
                  label="执行平台"
                  options={(business === "sms" || business === "promotion" && !editing) ? [
                    { value: "ios", label: "iPhone / iOS" },
                  ] : [
                    { value: "ios", label: "iPhone / iOS" },
                    { value: "web", label: "网页" },
                    { value: "all", label: "iOS 与网页" },
                  ]}
                  fieldProps={{ disabled: business === "sms", allowClear: business !== "sms" && (business !== "promotion" || editing) }}
                  rules={[{ required: true }]}
                />
              </Col>
              <Col xs={24} md={12}>
                <ProFormSelect
                  name="category"
                  label="任务分类"
                  options={[
                    { value: "experience", label: "体验任务" },
                    { value: "content", label: "内容任务" },
                    { value: "survey", label: "调研任务" },
                    { value: "onboarding", label: "新手任务" },
                    { value: "activity", label: "活动任务" },
                    { value: "verification", label: "核验任务" },
                  ]}
                  rules={[{ required: true, message: "请选择分类" }]}
                />
              </Col>
            </Row>
            {business !== "sms" && <ProFormText name="description_title" label="任务说明标题（选填）" placeholder="任务说明" fieldProps={{ maxLength: 60 }} />}
            <Form.Item name="description" label="任务说明" rules={[{ required: true, whitespace: true, message: "请填写任务说明" }]}><TaskTextEditor placeholder="介绍任务目的、执行内容与注意事项" /></Form.Item>
            {business === "sms" ? <details className="message-appearance-options"><summary>更多展示设置 <span>图标、标签与说明标题 · 选填</span></summary><div>{appearanceFields}</div></details> : appearanceFields}
          </div>
          <div
            className="editor-section"
            style={{ display: step === 1 ? "block" : "none" }}
          >
            <h3>执行步骤（选填）</h3>
            <p className="muted">无需自行编写时，使用平台默认步骤；有特殊操作要求时，可选择自定义步骤。没有添加自定义步骤时，会使用平台默认步骤。</p>
            <ProFormRadio.Group name="steps_mode" label="执行步骤来源" options={[{ value: "default", label: "使用平台默认步骤" }, { value: "custom", label: "自定义执行步骤" }]} />
            {stepMode === "custom" && <TaskStepsEditor allowVideo={business === "promotion"} assetURLs={resource.data?.asset_urls} optional onVideoBusy={(id,busy)=>setVideoUploads(current=>({...current,[id]:busy}))} />}
            {useDefaults && <section className="default-task-steps" aria-label="平台默认执行步骤">
              <Alert type="info" showIcon message="当前使用平台默认步骤" description="手机预览已同步展示默认内容。提交审核时会保存当时的平台默认步骤，后续修改不会影响已审核或已领取的任务。" />
              {policy.loading ? <Skeleton active paragraph={{ rows: 3 }} /> : policy.error ? <Alert type="error" showIcon message="默认步骤加载失败" description={policy.error} action={<Button onClick={() => void policy.reload()}>重试</Button>} /> : <>
                {!previewPayload.steps.length && <p className="muted">平台暂未配置默认执行步骤，可继续创建；会员端将不展示执行步骤。</p>}
                {previewPayload.steps.map((item, i) => <article key={item.id || i} className="default-task-step"><h4>步骤 {i + 1} · {item.title}</h4><TaskFormattedText text={item.description} /><Space wrap>{item.images?.map((value, j) => <MaterialImage key={j} value={value} assetURLs={previewAssets} width={100} />)}</Space><TaskVideoPreview video={item.videos?.[0]} assetURLs={previewAssets} />{item.links?.filter(link => /^https?:\/\//i.test(link.url)).map((link, j) => <p key={j}><a href={link.url} target="_blank" rel="noopener noreferrer">{link.label || "点击前往"}</a></p>)}</article>)}
                <Space wrap><Button onClick={() => void policy.reload()}>刷新默认步骤</Button><Button onClick={() => setPreviewOpen(true)}>查看手机预览</Button></Space>
              </>}
            </section>}

          </div>
          <div
            className="editor-section"
            style={{ display: step === 2 ? "block" : "none" }}
          >
            <h3>投放与报价</h3>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <ProFormDigit
                  name="price"
                  disabled={published}
                  label="单次奖励（元 / 每个完成名额）"
                  min={(policy.data?.price_range?.min_amount_minor ?? 1) / 100}
                  max={(policy.data?.price_range?.max_amount_minor ?? 100000000) / 100}
                  extra={policy.data?.price_range ? `平台允许范围：${money(policy.data.price_range.min_amount_minor)} 至 ${money(policy.data.price_range.max_amount_minor)}` : "正在加载平台价格区间…"}
                  fieldProps={{ precision: 2, addonBefore: "¥" }}
                  rules={[{ required: true, message: "请填写单次奖励" }, { validator: (_: unknown, value: number | undefined) => { const range = policy.data?.price_range; const amount = amountToMinor(value) ?? 0; return Number.isSafeInteger(amount) && amount > 0 && (published || range && amount >= range.min_amount_minor && amount <= range.max_amount_minor) ? Promise.resolve() : Promise.reject(new Error("请输入平台允许区间内的金额")); } }]}
                />
              </Col>
              <Col xs={24} md={12}>
                {type === "download" ? <Form.Item label="推广名额" extra="由各搜索关键词名额自动累加"><InputNumber value={quantity} disabled style={{ width: "100%" }} /></Form.Item> : type === "message_send" ? <Form.Item label="iMessage 任务名额" extra="名额由导入数量自动确定，每条信息对应一个名额，不可手动修改。"><InputNumber value={quantity} disabled precision={0} style={{ width: "100%" }} /></Form.Item> : <ProFormDigit
                  name="quantity"
                  disabled={published}
                  label="推广名额"
                  min={1}
                  max={1000000}
                  fieldProps={{ precision: 0 }}
                  extra="请填写正整数，提交时按名额冻结预算"
                  rules={[{ required: true, type: "integer", min: 1, message: "请填写大于 0 的整数名额" }]}
                />}
              </Col>
            </Row>
            <div className="budget-preview">
              <span>计划任务预算</span>
              <strong>{quantity ? money(budgetMinor) : type === "download" ? "请先分配关键词名额" : type === "message_send" ? "请先导入收件人与发送内容" : "请先填写任务名额"}</strong>
              <small>
                单次奖励 × 任务名额；提交审核时冻结，任务结束后按有效完成数量扣款
              </small>
            </div>
            <TaskFundingCheck budgetMinor={budgetMinor} alreadyFrozen={published} />
            <Row gutter={20}><Col xs={24} sm={12}><Form.Item name={["dates", 0]} label="开始时间（北京时间）" extra="留空表示发布后可开始"><DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} /></Form.Item></Col><Col xs={24} sm={12}><Form.Item name={["dates", 1]} label="结束时间（北京时间）" extra="留空表示不设固定截止时间" dependencies={[["dates", 0]]} rules={[{ validator: (_, end) => { const start = form.getFieldValue(["dates", 0]); return !start || !end || end.isAfter(start) ? Promise.resolve() : Promise.reject(new Error("结束时间必须晚于开始时间")); } }]}><DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} /></Form.Item></Col></Row>
            <Alert className="task-review-setting" type="info" showIcon message={`会员提交结果：${requireReview ? "需要平台人工审核" : "系统校验通过且未触发风控时自动结算"}`} description={business === "sms" ? "会员提交结果经平台审核通过后，结算对应奖励。" : "由平台配置，商户不可修改。提交发布审核时采用当时的设置；任务发布审核仍然保留，已领取任务沿用原版本规则。"} />
            {type === "download" && (
              <>
                <h3>下载应用配置</h3>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <ProFormDigit
                      name="app_id"
                      label="App Store ID"
                      min={1}
                      max={Number.MAX_SAFE_INTEGER}
                      fieldProps={{ precision: 0 }}
                      rules={[{ required: true, message: "请输入应用 ID" }]}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProFormText
                      name="bundle_id"
                      label="Bundle ID"
                      placeholder="com.example.app"
                      rules={[{ required: true, message: "请输入 Bundle ID" }]}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProFormText
                      name="app_name"
                      label="应用名称"
                      rules={[{ required: true, message: "请输入应用名称" }]}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProFormText name="app_version" label="应用版本" />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProFormDigit
                      name="app_price"
                      label="应用售价（元）"
                      min={0}
                      fieldProps={{ precision: 2 }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <ProFormText name="app_developer_name" label="开发者" />
                  </Col>
                </Row>
                <ProFormText
                  name="app_artwork_url"
                  label="应用图标链接"
                  rules={[{ type: "url", message: "请输入完整 URL" }]}
                />
                <h4>搜索关键词与配额</h4>
                <p className="muted">逐个设置关键词的任务份数，推广名额由关键词名额自动累加。删除关键词将移除它的配额；已有完成或占额的关键词仍受平台校验保护。</p>
                <Form.List name="keyword_quotas" rules={[{ validator: async (_, rows) => {
                  if (!rows?.length) throw new Error("至少添加一个搜索关键词");
                  const keys = rows.map((q: { keyword?: string }) => q.keyword?.trim().toLocaleLowerCase());
                  if (new Set(keys).size !== keys.length) throw new Error("搜索关键词不能重复");
                } }]}>
                  {(fields, { add, remove }, { errors }) => <>
                    {fields.map(field => <Row gutter={12} key={field.key} align="top">
                      <Col xs={13} sm={12}><Form.Item name={[field.name, "keyword"]} label="搜索关键词" rules={[{ required: true, whitespace: true, message: "请输入关键词" }]}><Input maxLength={100} disabled={published} /></Form.Item></Col>
                      <Col xs={7} sm={8}><Form.Item name={[field.name, "target_count"]} label="配额（份）" rules={[{ required: true, type: "integer", min: 1, message: "请输入正整数" }]}><InputNumber min={1} precision={0} disabled={published} style={{ width: "100%" }} /></Form.Item></Col>
                      <Col xs={4} sm={4}><Button aria-label={`删除关键词 ${field.name + 1}`} type="text" danger disabled={published} icon={<DeleteOutlined />} style={{ marginTop: 30 }} onClick={() => remove(field.name)} /></Col>
                    </Row>)}
                    <Space wrap style={{ marginBottom: 12 }}><Button icon={<PlusOutlined />} disabled={published} onClick={() => add({ keyword: "", target_count: 1 })}>添加关键词</Button><Button disabled={published || !fields.length || quantity < fields.length} onClick={() => form.setFieldValue("keyword_quotas", distributeQuotas((form.getFieldValue("keyword_quotas") || []).map((q: { keyword: string }) => q.keyword), quantity))}>平均分配当前名额</Button></Space>
                    <Form.Item noStyle shouldUpdate>{() => <p>已分配 {(form.getFieldValue("keyword_quotas") || []).reduce((sum: number, q: { target_count?: number }) => sum + (q.target_count || 0), 0)} / {quantity} 份</p>}</Form.Item>
                    <Form.ErrorList errors={errors} />
                  </>}
                </Form.List>
              </>
            )}
            <ProFormTextArea
              name="remarks"
              label="任务备注（选填）"
              fieldProps={{ rows: 3, maxLength: 2000 }}
            />
          </div>
          <div
            className="editor-section"
            style={{ display: step === 3 ? "block" : "none" }}
          >
            <div className="confirm-icon">
              <CheckCircleOutlined />
            </div>
            <h3>确认任务内容</h3>
            <p className="muted">
              保存草稿不冻结余额；提交审核后冻结预算，由平台确认发布。
            </p>
            <Form.Item noStyle shouldUpdate>
              {() => {
                const v = form.getFieldsValue(true);
                return (
                  <div className="confirm-summary">
                    <div>
                      <span>任务名称</span>
                      <strong>{v.title || "—"}</strong>
                    </div>
                    <div>
                      <span>任务类型</span>
                      <strong>{business === "sms" ? "iMessage 任务" : type ? typeLabels[type] : "—"}</strong>
                    </div>
                    <div>
                      <span>任务数量</span>
                      <strong>{quantity ? `${quantity} 份` : type === "download" ? "待分配关键词名额" : type === "message_send" ? "待导入" : "待填写名额"}</strong>
                    </div>
                    <div>
                      <span>单次奖励</span>
                      <strong>{money(amountToMinor(price))}</strong>
                    </div>
                    <div>
                      <span>计划任务预算</span>
                      <strong>
                        {quantity ? money(budgetMinor) : type === "download" ? "请先分配关键词名额" : type === "message_send" ? "请先导入收件人与发送内容" : "请先填写任务名额"}
                      </strong>
                    </div>
                    <div>
                      <span>执行步骤</span>
                      <strong>{useDefaults ? "平台默认" : "自定义"} · {previewPayload.steps.length} 步</strong>
                    </div>

                  </div>
                );
              }}
            </Form.Item>
            <TaskFundingCheck budgetMinor={budgetMinor} alreadyFrozen={published} />
            {business === "sms" && <Form.Item noStyle shouldUpdate>{() => {
              const imported = form.getFieldValue("message_import");
              return imported ? <MessageImport readOnly value={imported} /> : null;
            }}</Form.Item>}
            <Form.Item noStyle shouldUpdate>{() => <TaskPreview assetURLs={previewAssets} payload={previewPayload} />}</Form.Item>
            <ProFormTextArea
              name="change_note"
              label={editing ? "本次修改说明" : "提交说明（选填）"}
              fieldProps={{ rows: 3, maxLength: 1000 }}
              rules={
                editing
                  ? [
                      {
                        required: true,
                        whitespace: true,
                        message: "请说明本次修改内容",
                      },
                    ]
                  : []
              }
            />
            <Alert
              type="info"
              showIcon
              message="任务提交后进入平台审核，不会立即发布。审核结果将通过消息通知告知。"
            />
          </div>
        </ProForm>
        {saveError && (
          <Alert
            showIcon
            type="error"
            message={saveError}
            style={{ marginBottom: 20 }}
          />
        )}
        <div className="editor-actions">
          <span className="muted">
            {`第 ${step + 1} 步，共 4 步`}
          </span>
          <Space wrap>
            <Button onClick={() => setPreviewOpen(true)}>会员视角预览</Button>
            {step > 0 && (
              <Button onClick={() => setStep(step - 1)} disabled={importBusy || busy}>
                上一步
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                iconPosition="end"
                disabled={importBusy || busy}
                onClick={() => void next()}
              >
                下一步
              </Button>
            ) : (
              <>
                <Button
                  icon={<SaveOutlined />}
                  loading={busy}
                  disabled={importBusy || agent.business_frozen}
                  onClick={() => void save(false)}
                >
                  保存草稿
                </Button>
                <Button
                  type="primary"
                  loading={busy}
                  disabled={importBusy || agent.business_frozen || agent.publish_restricted || useDefaults && (policy.loading || !!policy.error || !policy.data)}
                  onClick={() => void save(true)}
                >
                  提交平台审核
                </Button>
              </>
            )}
          </Space>
        </div>
      </Card>{business === "promotion" ? <aside className="task-preview-aside"><div className="task-preview-caption">手机实时预览<span>随输入更新</span></div><TaskPhonePreview assetURLs={previewAssets} payload={previewPayload} /><p className="task-preview-note">按照会员端的说明、规则、步骤顺序展示。内部备注不展示；领取按钮仅为效果预览。</p><Button block onClick={() => setGuideOpen(true)}>查看完整图文示例</Button></aside> : <aside className="message-task-aside">
        <div className="message-task-overview"><div className="message-overview-heading"><h2>任务概览</h2><span>随填写更新</span></div>
          <h3>{allValues.title?.trim() || "尚未填写任务名称"}</h3>
          <p className="message-overview-platform"><MobileOutlined /> iPhone / iOS</p>
          <dl><div><dt>已导入信息</dt><dd>{importedCount ? <>{importedCount.toLocaleString()} <small>条</small></> : <span>待导入</span>}</dd></div><div><dt>单次奖励</dt><dd>{money(amountToMinor(price))}</dd></div><div><dt>任务名额</dt><dd>{importedCount || resource.data?.formal_task_id ? <>{quantity.toLocaleString()} <small>份</small></> : <span>由导入数量确定</span>}</dd></div></dl>
          <div className="message-overview-budget"><span>计划任务预算</span><strong>{importedCount || resource.data?.formal_task_id ? money(budgetMinor) : "—"}</strong><small>按审核通过的完成数量结算</small></div>
          <Button block onClick={() => setPreviewOpen(true)}>查看会员端展示</Button>
        </div>
        <div className="message-publish-note"><SafetyCertificateOutlined /><div><h3>审核通过后发布</h3><p>手机号码与邮箱均可接收，收件人需已开通 iMessage。任务提交后，我们会通过消息通知审核结果。</p></div></div>
      </aside>}</div>
    </div>
  );
}
